import { HarnessControl } from "@/components/MessageInput/hooks/useHarnessControl";
import { Button } from "@/components/ui/button";
import {
  AGENT_IDS,
  AGENT_META,
  AGENT_ORDER,
  AgentId,
  findAgent,
} from "@/constants/agents";
import { cn } from "@/utils";
import { trackComposerControlChanged } from "@/utils/analytics";
import type { ReactNode } from "react";
import { ConnectAgentButton } from "./ConnectAgentButton";

/**
 * The picked subscription's connect prompt under the agent cards, where a chat starts. Copy is the
 * host's.
 */
export function ConnectStrip({
  agent,
  onConnect,
  children,
  className,
}: {
  agent: AgentId;
  onConnect: () => void;
  children: ReactNode;
  className?: string;
}) {
  const { Logo, providerName } = AGENT_META[agent];
  return (
    <div
      className={cn(
        "flex w-full items-center gap-3 rounded-lg bg-n-05 px-3 py-2.5 text-left",
        className,
      )}
    >
      <Logo className="size-4 shrink-0 text-n-70" />
      <div className="flex-1 text-xs leading-snug text-n-60">{children}</div>
      <Button
        className="h-7 min-h-7 shrink-0 rounded-sm px-3 text-xs"
        onClick={onConnect}
      >
        Connect {providerName}
      </Button>
    </div>
  );
}

/** The same prompt as one line in the slot above a composer, where there are no cards. */
export function ComposerConnectStrip({
  agent,
  locked,
  onConnect,
  className,
}: {
  agent: AgentId;
  /** The chat has started: it asks to continue rather than start. */
  locked?: boolean;
  onConnect: () => void;
  className?: string;
}) {
  const { Logo, providerName, plan } = AGENT_META[agent];
  const sentence = `${providerName} isn’t connected. Connect your ${plan} plan to ${locked ? "continue" : "start"} this chat.`;
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-xl border border-n-10 bg-n-05 px-2.5 py-1.5 text-xs",
        className,
      )}
    >
      <Logo className="size-3.5 shrink-0 text-n-70" />
      <span className="min-w-0 flex-1 truncate text-n-70" title={sentence}>
        {sentence}
      </span>
      <Button
        className="h-6 min-h-6 shrink-0 rounded-md px-2.5 text-xs"
        onClick={onConnect}
      >
        Connect {providerName}
      </Button>
    </div>
  );
}

function AgentCard({
  agent,
  selected,
  connected,
  account,
  onSelect,
  onConnect,
}: {
  agent: AgentId;
  selected: boolean;
  connected: boolean;
  account?: string | null;
  onSelect: () => void;
  onConnect: () => void;
}) {
  const { Logo, label, cardSubline, plan } = AGENT_META[agent];
  const needsConnect = agent !== AGENT_IDS.COHESIVE && !connected;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      data-state={selected ? "active" : "inactive"}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "flex min-w-0 cursor-pointer items-center gap-3 rounded-xl border-2 border-n-10 bg-card px-3 py-2.5 text-left outline-none transition-[border-color,box-shadow] duration-150 ease-out sm:p-3",
        "sm:flex-1 sm:flex-col sm:items-start sm:gap-2",
        "hover:border-n-30 focus-visible:border-n-30 data-[state=active]:border-rust data-[state=active]:shadow-[0_0_0_3px_color-mix(in_oklab,var(--rust)_14%,transparent)]",
      )}
    >
      <Logo className="size-5 shrink-0 text-n-80" />
      <div className="min-w-0 flex-1 sm:w-full sm:flex-none">
        <div className="text-sm font-medium text-n-90">{label}</div>
        <div className="truncate text-xs leading-snug text-muted-foreground">
          {connected && account ? (
            account
          ) : (
            <>
              {/* A full-width row has room to name the plan. */}
              <span className="sm:hidden">
                {plan ? `Your ${plan} plan` : cardSubline}
              </span>
              <span className="hidden sm:inline">{cardSubline}</span>
            </>
          )}
        </div>
      </div>
      {needsConnect && <ConnectAgentButton onClick={onConnect} />}
    </div>
  );
}

/**
 * Where a chat starts: which agent runs it, as three cards. Drives the same control as the chip
 * under the composer, so the two never disagree.
 */
export function AgentChooser({
  control,
  onConnect,
}: {
  control: HarnessControl;
  onConnect: (agent: AgentId) => void;
}) {
  const pick = (agent: AgentId) => {
    if (agent !== control.agent) {
      trackComposerControlChanged({ control: "agent", value: agent });
    }
    control.setAgent(agent);
  };
  const { plan } = AGENT_META[control.agent];

  return (
    <>
      <p className="mt-1 text-xs text-n-50">
        Which agent should run this chat?
        <span className="hidden sm:inline">
          {" "}
          It’s fixed once the chat starts.
        </span>
      </p>
      <div className="mt-5 flex w-full flex-col gap-2 sm:flex-row">
        {AGENT_ORDER.map((agent) => {
          const capability = findAgent(control.agents, agent);
          if (!capability) return null;
          return (
            <AgentCard
              key={agent}
              agent={agent}
              selected={agent === control.agent}
              connected={capability.connected}
              account={capability.account_email}
              onSelect={() => pick(agent)}
              onConnect={() => {
                pick(agent);
                onConnect(agent);
              }}
            />
          );
        })}
      </div>
      {!control.connected && (
        <ConnectStrip
          className="mt-3"
          agent={control.agent}
          onConnect={() => onConnect(control.agent)}
        >
          {AGENT_META[control.agent].label} runs on your {plan} plan. Connect
          once; it stays connected.
        </ConnectStrip>
      )}
    </>
  );
}
